import type {
  IStorageProvider,
  StorageProvider,
  FileToUpload,
  UploadedFile,
  DownloadedFile,
  UploadProgress
} from '../types.js'

/**
 * Base abstract class for storage providers
 */
export abstract class BaseStorageProvider implements IStorageProvider {
  abstract readonly name: StorageProvider
  protected verbose: boolean

  constructor(verbose: boolean = false) {
    this.verbose = verbose
  }

  abstract initialize(): Promise<void>
  abstract uploadFile(file: FileToUpload): Promise<UploadedFile>
  abstract downloadFile(remotePath: string): Promise<DownloadedFile>
  abstract fileExists(remotePath: string): Promise<boolean>
  abstract deleteFile(remotePath: string): Promise<void>
  abstract listFiles(remotePath?: string): Promise<string[]>
  abstract cleanup(): Promise<void>

  /**
   * Upload multiple files with progress tracking
   */
  async uploadFiles(
    files: FileToUpload[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFile[]> {
    const results: UploadedFile[] = []
    let filesUploaded = 0

    for (const file of files) {
      if (onProgress) {
        onProgress({
          currentFile: file.path,
          filesUploaded,
          totalFiles: files.length,
          bytesUploaded: 0,
          totalBytes: 0
        })
      }

      const result = await this.uploadFile(file)
      results.push(result)
      filesUploaded++

      if (onProgress) {
        onProgress({
          currentFile: file.path,
          filesUploaded,
          totalFiles: files.length,
          bytesUploaded: result.size,
          totalBytes: result.size
        })
      }

      if (this.verbose) {
        console.log(`✓ Uploaded ${file.path} → ${result.remotePath}`)
      }
    }

    return results
  }

  /**
   * Download multiple files
   */
  async downloadFiles(files: string[]): Promise<DownloadedFile[]> {
    const results: DownloadedFile[] = []

    for (const remotePath of files) {
      const result = await this.downloadFile(remotePath)
      results.push(result)

      if (this.verbose) {
        console.log(`✓ Downloaded ${remotePath} (${result.size} bytes)`)
      }
    }

    return results
  }

  /**
   * Log message if verbose mode is enabled
   */
  protected log(message: string): void {
    if (this.verbose) {
      console.log(`[${this.name}] ${message}`)
    }
  }

  /**
   * Log error
   */
  protected logError(message: string, error?: Error): void {
    console.error(`[${this.name}] ERROR: ${message}`)
    if (error && this.verbose) {
      console.error(error)
    }
  }
}
